import { CheckCircle2, CircleAlert, FileText, Loader2, Pause, Play, ShieldAlert, Upload, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useInterruptores } from '../api/interruptores'
import { type FicheroEnPantalla, type LoteDelNucleo, terminado, type Visibilidad } from '../api/lotes'
import { tamano } from '../api/medios'
import { Aviso, Boton, Casilla, claseDeEntrada, clases } from '../componentes/ui'
import { SubidaDeUnaImagen } from './SubidaSencilla'
import { type SubidaPorLotes, useSubidaPorLotes } from './useSubidaPorLotes'

/**
 * Las piezas de la subida por lotes (plan del CMS dinámico §13): elegir o arrastrar muchos
 * ficheros, ver cómo va cada uno, pausar, reanudar y cancelar; y retomar lo que quedó a medias
 * con la pestaña cerrada. Lo que se admite lo decide el núcleo y lo dice al crear el lote: aquí no
 * hay copia de la lista de formatos.
 */
const ESTADOS: Record<FicheroEnPantalla['estado'], { texto: string; tono: 'error' | 'neutro' | 'ok' | 'trabajando' }> = {
  pendiente: { texto: 'En espera', tono: 'neutro' },
  reduciendo: { texto: 'Reduciendo', tono: 'trabajando' },
  subiendo: { texto: 'Subiendo', tono: 'trabajando' },
  analizando: { texto: 'Analizando', tono: 'trabajando' },
  procesando: { texto: 'Procesando', tono: 'trabajando' },
  listo: { texto: 'Listo', tono: 'ok' },
  duplicado: { texto: 'Ya lo tenías', tono: 'ok' },
  fallido: { texto: 'No se ha subido', tono: 'error' },
  bloqueado: { texto: 'Bloqueado', tono: 'error' },
  cancelado: { texto: 'Cancelado', tono: 'neutro' },
}

const IconoDeEstado = ({ estado }: { estado: FicheroEnPantalla['estado'] }) => {
  const tono = ESTADOS[estado].tono
  if (estado === 'bloqueado') return <ShieldAlert aria-hidden className="size-4 shrink-0 text-error" />
  if (tono === 'ok') return <CheckCircle2 aria-hidden className="size-4 shrink-0 text-ok" />
  if (tono === 'error') return <CircleAlert aria-hidden className="size-4 shrink-0 text-error" />
  if (tono === 'trabajando') return <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-violeta" />
  return <FileText aria-hidden className="size-4 shrink-0 text-tenue" />
}

export const FilaDeFichero = ({ f }: { f: FicheroEnPantalla }) => (
  <li className="flex flex-col gap-1 border-b border-linea px-3 py-2 last:border-b-0" data-estado={f.estado}>
    <div className="flex items-center gap-2">
      <IconoDeEstado estado={f.estado} />
      <span className="min-w-0 flex-1 truncate text-[13px]">{f.nombre}</span>
      <span className="shrink-0 text-xs text-tenue tabular-nums">{tamano(f.tamano)}</span>
      <span className={clases('w-28 shrink-0 text-right text-xs', ESTADOS[f.estado].tono === 'error' ? 'text-error' : 'text-tinta-2')}>{ESTADOS[f.estado].texto}</span>
    </div>
    {f.estado === 'subiendo' && (
      <span aria-label={`Subido el ${Math.round(f.progreso * 100)} %`} className="h-1 overflow-hidden rounded-full bg-papel-3" role="progressbar" aria-valuemax={100} aria-valuemin={0} aria-valuenow={Math.round(f.progreso * 100)}>
        <span className="block h-full rounded-full bg-violeta transition-[width]" style={{ width: `${Math.round(f.progreso * 100)}%` }} />
      </span>
    )}
    {f.motivo && (f.estado === 'fallido' || f.estado === 'bloqueado' || f.estado === 'duplicado' || f.estado === 'pendiente' || f.estado === 'analizando') && <span className="pl-6 text-xs text-tinta-2">{f.motivo}</span>}
  </li>
)

/** La zona donde se sueltan o eligen ficheros. */
export const ZonaDeFicheros = ({ alElegir, varios = true, acepta, texto }: { acepta?: string; alElegir: (f: File[]) => void; texto?: string; varios?: boolean }) => {
  const [encima, setEncima] = useState(false)
  return (
    <label
      className={clases(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-caja border-2 border-dashed px-4 py-8 text-center transition-colors',
        encima ? 'border-violeta bg-violeta-suave' : 'border-linea-fuerte bg-papel-2 hover:border-violeta',
      )}
      onDragLeave={() => setEncima(false)}
      onDragOver={(e) => {
        e.preventDefault()
        setEncima(true)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setEncima(false)
        alElegir([...(e.dataTransfer.files ?? [])])
      }}
    >
      <Upload className="size-6 text-tenue" />
      <span className="text-[13px] font-medium">{texto ?? (varios ? 'Arrastra aquí tus ficheros o pulsa para elegirlos' : 'Arrastra un fichero o pulsa para elegirlo')}</span>
      <span className="text-xs text-tenue">Imágenes, SVG y documentos (PDF, Word, Excel, PowerPoint, CSV, texto). Lo que no valga te lo diremos.</span>
      <input
        accept={acepta}
        aria-label={varios ? 'Elegir ficheros' : 'Elegir un fichero'}
        className="sr-only"
        multiple={varios}
        onChange={(e) => {
          alElegir([...(e.target.files ?? [])])
          e.target.value = ''
        }}
        type="file"
      />
    </label>
  )
}

/** Público o privado al subir. Lo privado solo se ofrece a quien puede verlo. */
export const ElegirVisibilidad = ({ valor, alCambiar, permitePrivado }: { alCambiar: (v: Visibilidad) => void; permitePrivado: boolean; valor: Visibilidad }) => (
  <fieldset className="flex flex-col gap-1.5">
    <legend className="mb-1 text-[13px] font-medium">Visibilidad</legend>
    <label className="flex items-start gap-2 text-[13px]">
      <input checked={valor === 'publico'} className="mt-0.5 accent-violeta" name="visibilidad" onChange={() => alCambiar('publico')} type="radio" />
      <span>
        <span className="font-medium">Público</span> <span className="text-tenue">— tiene dirección en tu web y se puede poner en cualquier página.</span>
      </span>
    </label>
    <label className={clases('flex items-start gap-2 text-[13px]', !permitePrivado && 'opacity-50')}>
      <input checked={valor === 'privado'} className="mt-0.5 accent-violeta" disabled={!permitePrivado} name="visibilidad" onChange={() => alCambiar('privado')} type="radio" />
      <span>
        <span className="font-medium">Privado</span>{' '}
        <span className="text-tenue">
          — solo se descarga con un enlace que caduca a los diez minutos.{!permitePrivado && ' Pídele permiso al propietario de la cuenta.'}
        </span>
      </span>
    </label>
  </fieldset>
)

/**
 * El diálogo de subir muchos (contenido): elegir, opciones y la lista con su progreso. La subida
 * vive en `subida` (la página), así que cerrar el diálogo no la corta.
 */
export const SubidaMultiple = ({ subida, permitePrivado, retomando }: { permitePrivado: boolean; retomando?: LoteDelNucleo | null; subida: SubidaPorLotes }) => {
  const [elegidos, setElegidos] = useState<File[]>([])
  const [visibilidad, setVisibilidad] = useState<Visibilidad>('publico')
  const [carpeta, setCarpeta] = useState('')
  const [igualmente, setIgualmente] = useState(false)
  const [aviso, setAviso] = useState<null | string>(null)
  const enMarcha = subida.fase !== 'inactiva' || subida.ficheros.length > 0

  if (retomando && !enMarcha) {
    const faltan = retomando.sinSubir ?? retomando.total
    return (
      <div className="flex flex-col gap-4">
        <Aviso tono="info">
          Esta subida se quedó a medias: {faltan === 1 ? 'falta un fichero' : `faltan ${faltan} ficheros`}. El navegador ya no los tiene: elígelos otra vez (los mismos) y seguimos donde se quedó.
        </Aviso>
        <ul className="max-h-40 overflow-auto rounded-caja border border-linea text-[13px]">
          {retomando.ficheros.map((f, i) => (
            <li className="flex justify-between border-b border-linea px-3 py-1.5 last:border-b-0" key={`${i}:${f.nombre}`}>
              <span className="truncate">{f.nombre}</span>
              <span className="text-xs text-tenue tabular-nums">{tamano(f.tamano)}</span>
            </li>
          ))}
        </ul>
        <ZonaDeFicheros
          alElegir={(fs) =>
            void subida.retomar(retomando.id, fs).then(({ casados, sinCasar }) =>
              setAviso(casados === 0 ? 'Ninguno de los ficheros elegidos coincide con los que faltaban (por nombre y tamaño).' : sinCasar.length > 0 ? `No coinciden con lo que faltaba: ${sinCasar.join(', ')}.` : null),
            )
          }
          texto="Elige otra vez los ficheros que faltaban"
        />
        {aviso && <Aviso>{aviso}</Aviso>}
      </div>
    )
  }

  if (!enMarcha) {
    return (
      <form
        className="flex flex-col gap-4"
        id="subida-por-lotes"
        onSubmit={(e) => {
          e.preventDefault()
          if (elegidos.length === 0) return
          void subida.empezar(elegidos, { visibilidad, carpeta: carpeta.trim(), subirIgualmente: igualmente })
          // Lo elegido ya va en el lote: «Subir más» empieza con la lista vacía, no con lo que ya se subió.
          setElegidos([])
        }}
      >
        <ZonaDeFicheros alElegir={(fs) => setElegidos((antes) => [...antes, ...fs])} />
        {elegidos.length > 0 && (
          <ul className="max-h-56 overflow-auto rounded-caja border border-linea">
            {elegidos.map((f, i) => (
              <li className="flex items-center gap-2 border-b border-linea px-3 py-1.5 text-[13px] last:border-b-0" key={`${i}:${f.name}:${f.size}`}>
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <span className="text-xs text-tenue tabular-nums">{tamano(f.size)}</span>
                <button aria-label={`Quitar ${f.name}`} className="rounded p-0.5 text-tenue hover:text-error" onClick={() => setElegidos((t) => t.filter((_, j) => j !== i))} type="button">
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <ElegirVisibilidad alCambiar={setVisibilidad} permitePrivado={permitePrivado} valor={visibilidad} />
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Carpeta (opcional)</span>
          <input className={claseDeEntrada()} maxLength={120} onChange={(e) => setCarpeta(e.target.value)} placeholder="Por ejemplo «Catálogo 2026»" value={carpeta} />
        </label>
        <label className="flex items-center gap-2 text-[13px]">
          <Casilla marcada={igualmente} onChange={(e) => setIgualmente(e.target.checked)} />
          Subir aunque ya esté en la biblioteca
        </label>
        {subida.error && <Aviso tono="error">{subida.error}</Aviso>}
        <Boton disabled={elegidos.length === 0} icono={<Upload />} tipo="primario" type="submit">
          {elegidos.length === 0 ? 'Elige los ficheros' : elegidos.length === 1 ? 'Subir 1 fichero' : `Subir ${elegidos.length} ficheros`}
        </Boton>
      </form>
    )
  }

  const { cuentas } = subida
  return (
    <div className="flex flex-col gap-3">
      <p aria-live="polite" className="text-[13px] text-tinta-2" role="status">
        {subida.fase === 'creando'
          ? 'Preparando la subida…'
          : subida.fase === 'terminado'
            ? `Terminado: ${cuentas.listos} ${cuentas.listos === 1 ? 'listo' : 'listos'}${cuentas.duplicados ? `, ${cuentas.duplicados} que ya tenías` : ''}${cuentas.fallidos ? `, ${cuentas.fallidos} sin subir` : ''}.`
            : subida.fase === 'pausado'
              ? `En pausa: ${cuentas.listos} de ${cuentas.total} listos. Lo que ya estaba subiendo termina.`
              : subida.fase === 'cancelado'
                ? 'Subida cancelada. Lo que ya estaba listo se queda en la biblioteca.'
                : `${cuentas.listos} de ${cuentas.total} listos${cuentas.fallidos ? ` · ${cuentas.fallidos} con problemas` : ''}`}
      </p>
      <ul className="max-h-[50vh] overflow-auto rounded-caja border border-linea">
        {subida.ficheros.map((f) => (
          <FilaDeFichero f={f} key={`${f.indice}:${f.nombre}`} />
        ))}
      </ul>
      {aviso && <Aviso>{aviso}</Aviso>}
      {subida.error && <Aviso tono="error">{subida.error}</Aviso>}
      <div className="flex flex-wrap gap-2">
        {subida.fase === 'subiendo' && cuentas.enCurso > 0 && (
          <Boton icono={<Pause />} onClick={() => void subida.pausar()}>
            Pausar
          </Boton>
        )}
        {subida.fase === 'pausado' && (
          <Boton icono={<Play />} onClick={() => void subida.reanudar()} tipo="primario">
            Reanudar
          </Boton>
        )}
        {(subida.fase === 'subiendo' || subida.fase === 'pausado') && cuentas.enCurso > 0 && (
          <Boton icono={<X />} onClick={() => void subida.cancelar()} tipo="peligro">
            Cancelar
          </Boton>
        )}
        {(subida.fase === 'terminado' || subida.fase === 'cancelado') && (
          <Boton icono={<Upload />} onClick={subida.limpiar}>
            Subir más
          </Boton>
        )}
      </div>
    </div>
  )
}

/**
 * Subir **un** fichero y elegirlo (los campos de imagen o archivo de un editor): un lote de uno,
 * por el mismo camino que la biblioteca. Cuando el núcleo lo deja listo —o dice que ya lo tenías—,
 * se elige.
 */
export const SubidaDeUnFichero = (p: { alSubir: (medio: number) => void; documentos: boolean; espacio: number; permitePrivado: boolean; sitio: number }) => {
  // Con la biblioteca v2 apagada en la instalación (lo dice el núcleo), la subida de siempre: una imagen, pública.
  const { mediosV2 } = useInterruptores()
  return mediosV2 ? <SubidaDeUnFicheroPorLotes {...p} /> : <SubidaDeUnaImagen alSubir={(m) => p.alSubir(m.id)} espacio={p.espacio} sitio={p.sitio} />
}

const SubidaDeUnFicheroPorLotes = ({ sitio, espacio, documentos, permitePrivado, alSubir }: { alSubir: (medio: number) => void; documentos: boolean; espacio: number; permitePrivado: boolean; sitio: number }) => {
  const subida = useSubidaPorLotes(sitio, espacio)
  const [visibilidad, setVisibilidad] = useState<Visibilidad>('publico')
  const avisado = useRef(false)
  const f = subida.ficheros[0]
  useEffect(() => {
    if (!f || avisado.current) return
    if ((f.estado === 'listo' || f.estado === 'duplicado') && f.medio) {
      avisado.current = true
      alSubir(f.medio)
    }
  }, [f, alSubir])
  if (!f) {
    return (
      <div className="flex flex-col gap-3">
        <ZonaDeFicheros acepta={documentos ? undefined : 'image/*'} alElegir={(fs) => fs[0] && void subida.empezar([fs[0]], { visibilidad, carpeta: '', subirIgualmente: false })} varios={false} />
        {documentos && <ElegirVisibilidad alCambiar={setVisibilidad} permitePrivado={permitePrivado} valor={visibilidad} />}
        {subida.error && <Aviso tono="error">{subida.error}</Aviso>}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <ul className="rounded-caja border border-linea">
        <FilaDeFichero f={f} />
      </ul>
      {terminado(f.estado) && f.estado !== 'listo' && f.estado !== 'duplicado' && (
        <Boton icono={<Upload />} onClick={subida.limpiar}>
          Elegir otro
        </Boton>
      )}
    </div>
  )
}
